import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const BakingProcess = () => {
  const processSteps = [
  {
    time: "4:00 AM",
    title: "Pre-Dawn Preparation",
    description: "Our bakers arrive before sunrise to begin the day's work. The first task is checking our natural yeast starters—some over 40 years old—ensuring they're active and ready. We prepare the mise en place, weighing ingredients with precision and organizing workstations for the day ahead.",
    image: "https://images.unsplash.com/photo-1593101611462-03dca1dce54d",
    imageAlt: "Professional baker in white uniform and chef's hat carefully checking natural sourdough starter in glass jar under soft pre-dawn kitchen lighting, with organized ingredients and equipment visible on stainless steel counter",
    icon: "Clock",
    highlights: ["Natural starter activation", "Ingredient preparation", "Equipment setup"]
  },
  {
    time: "5:00 AM",
    title: "Dough Development",
    description: "The mixing begins. We use traditional spiral mixers that develop gluten slowly, preserving the flour's natural characteristics. Each dough type requires different mixing times and techniques—our focaccia dough is mixed gently to maintain airiness, while our bread dough needs more development for structure.",
    image: "https://images.unsplash.com/photo-1501870969985-2f9d06dcf109",
    imageAlt: "Close-up of baker's hands expertly kneading elastic bread dough on flour-dusted marble work surface in professional bakery kitchen, showing proper technique and dough texture development under bright overhead lighting",
    icon: "Wheat",
    highlights: ["Spiral mixing technique", "Gluten development", "Temperature control"]
  },
  {
    time: "6:30 AM",
    title: "Shaping & Proofing",
    description: "This is where artistry meets science. Each piece is hand-shaped with techniques passed down through generations. Cornetti are rolled and curved into crescents, focaccia is stretched and dimpled, bread loaves are scored with traditional patterns. Everything then goes into our temperature-controlled proofing chambers.",
    image: "https://images.unsplash.com/photo-1590368746266-5efb0222a091",
    imageAlt: "Skilled baker's hands delicately shaping croissant dough into perfect crescent forms on professional baking sheet, surrounded by rows of precisely formed pastries ready for proofing in bright artisan bakery workspace",
    icon: "Hand",
    highlights: ["Hand-shaping techniques", "Traditional scoring", "Controlled proofing"]
  },
  {
    time: "7:30 AM",
    title: "The Baking",
    description: "Our stone deck ovens are heated to precise temperatures—different for each product. We inject steam at the start of baking to create that perfect crust. The bakery fills with the aroma of fresh bread as golden loaves emerge. Timing is critical; we monitor each batch closely, looking for that perfect color and listening for the right sound when we tap the bottom.",
    image: "https://images.unsplash.com/photo-1722635044899-5a48fa65117f",
    imageAlt: "Professional baker using long wooden peel to carefully place shaped bread loaves into large commercial stone deck oven with visible flames, steam rising from freshly baked golden loaves on cooling racks in background",
    icon: "Flame",
    highlights: ["Stone deck ovens", "Steam injection", "Temperature precision"]
  },
  {
    time: "8:00 AM",
    title: "Finishing Touches",
    description: "Fresh from the oven, products receive their final touches. Cornetti are brushed with simple syrup for shine, focaccia gets a final drizzle of olive oil, pastries are filled with fresh cream. Everything is arranged in our display cases while still warm, ready for our first customers who've been waiting at the door.",
    image: "https://images.unsplash.com/photo-1623814877285-846d8f309845",
    imageAlt: "Pastry chef in professional kitchen carefully piping fresh cream filling into golden baked cornetti pastries on marble counter, with finished products being arranged on elegant serving platters under warm bakery lighting",
    icon: "Sparkles",
    highlights: ["Final glazing", "Fresh fillings", "Display presentation"]
  },
  {
    time: "Throughout Day",
    title: "Continuous Baking",
    description: "We don't bake everything at once. Throughout the day, fresh batches emerge from our ovens every hour. This ensures customers always get products at peak freshness. Our afternoon baking includes focaccia for lunch sandwiches and a second round of pastries for the after-work crowd.",
    image: "https://images.unsplash.com/photo-1721633616494-d096d7c35c74",
    imageAlt: "Busy artisan bakery interior showing multiple bakers working simultaneously at different stations, fresh breads cooling on racks, customers browsing display cases, and warm afternoon sunlight streaming through windows creating inviting atmosphere",
    icon: "RefreshCw",
    highlights: ["Hourly fresh batches", "Peak freshness guarantee", "Continuous production"]
  }];


  return (
    <section className="py-20 bg-background">
      <div className="container-custom">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 mb-4">
            <Icon name="ChefHat" size={18} color="var(--color-accent)" />
            <span className="text-sm font-medium text-accent">Behind the Scenes</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground mb-4">
            A Day in Our Bakery
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            From pre-dawn preparation to the final customer of the day, discover the craftsmanship and dedication behind every product
          </p>
        </div>

        <div className="space-y-12">
          {processSteps?.map((step, index) =>
          <div
            key={index}
            className={`grid lg:grid-cols-2 gap-8 items-center ${
            index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`
            }>

              <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                <div className="relative rounded-2xl overflow-hidden shadow-warm-lg group">
                  <div className="aspect-[4/3] overflow-hidden">
                    <Image
                    src={step?.image}
                    alt={step?.imageAlt}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-background/90 backdrop-blur-sm flex items-center justify-center">
                        <Icon name={step?.icon} size={24} color="var(--color-primary)" />
                      </div>
                      <span className="font-accent text-2xl text-white font-bold">
                        {step?.time}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                <div className="bg-card rounded-2xl p-8 shadow-warm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon name={step?.icon} size={28} color="var(--color-primary)" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-primary">{step?.time}</span>
                      <h3 className="font-serif text-2xl font-bold text-foreground">
                        {step?.title}
                      </h3>
                    </div>
                  </div>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    {step?.description}
                  </p>

                  <div className="space-y-2">
                    {step?.highlights?.map((highlight, idx) =>
                  <div key={idx} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                          <Icon name="Check" size={14} color="var(--color-accent)" />
                        </div>
                        <span className="text-sm text-foreground font-medium">
                          {highlight}
                        </span>
                      </div>
                  )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-16 bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 md:p-12 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
              <Icon name="Heart" size={40} color="var(--color-primary)" />
            </div>
            <h3 className="font-serif text-3xl font-bold text-foreground mb-4">
              Freshly Baked, Made with Heart
            </h3>
            <p className="text-lg text-muted-foreground mb-6">
              Every product that leaves our bakery represents hours of careful preparation, traditional techniques, and genuine passion. We don't take shortcuts because we believe you deserve the authentic Italian experience—the same quality Giuseppe and Maria promised in 1985.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-background">
                <Icon name="Clock" size={16} color="var(--color-primary)" />
                <span className="text-foreground font-medium">Fresh Every Hour</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-background">
                <Icon name="Leaf" size={16} color="var(--color-primary)" />
                <span className="text-foreground font-medium">Natural Ingredients</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-background">
                <Icon name="Award" size={16} color="var(--color-primary)" />
                <span className="text-foreground font-medium">Traditional Methods</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default BakingProcess;